/**
 * hubNavigation — Surface Layer (/01)
 *
 * The two SEPARATE navigation models, one per plane. The shell renders exactly
 * one model (the active plane's) — DevHub and UserHub menus never mix, so a
 * Workspace user can never see a Developer Hub item and vice-versa. Plane
 * switching (for identities that can enter both) is an explicit control, not a
 * menu entry.
 *
 * Items map to REAL, wired routes. Destinations not yet built are marked
 * `comingSoon` and rendered disabled — the intended information architecture is
 * shown honestly without faking completed functionality (NO_FAKE_READY).
 *
 * Icons are restricted to the set already verified-imported by the live shell
 * (`@carbon/icons-react`) to avoid unverified Carbon imports.
 */

import {
  Application,
  Chat,
  ColorPalette,
  Dashboard,
  Document,
  Events,
  Flow,
  Notification,
  Report,
  Settings,
  Tools,
} from '@carbon/icons-react';
import type { RoleClass } from '../../components/navigation/roleNavigation';
import type { HubPlane } from './hubPlane';

export interface NavModelItem {
  to: string;
  label: string;
  icon: typeof Dashboard;
  plane: HubPlane;
  /** When set, only these role classes see the item (within-plane filter). */
  roleClasses?: RoleClass[];
  /** Honest disabled state for a destination that is specified but not built. */
  comingSoon?: boolean;
}

/**
 * DevHub (ControlPlane / operator) navigation. The Developer Hub cockpit
 * (`/control-plane`) hosts tenants, the ActionFrame ledger, the approval queue,
 * evidence/traces, FinOps and invites as tabs today; granular routes that do
 * not exist yet are marked coming-soon.
 */
export const DEVHUB_NAV: NavModelItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: Dashboard, plane: 'devhub' },
  { to: '/devhub', label: 'Developer Hub', icon: Tools, plane: 'devhub' },
  { to: '/devhub/runtime', label: 'Runtime', icon: Events, plane: 'devhub' },
  { to: '/devhub/ai', label: 'AI Orchestrator', icon: Flow, plane: 'devhub' },
  { to: '/devhub/governance', label: 'Governance', icon: Document, plane: 'devhub' },
  { to: '/devhub/resources', label: 'Resources', icon: Tools, plane: 'devhub' },
  { to: '/devhub/release', label: 'Release', icon: Report, plane: 'devhub' },
  { to: '/devhub/audit', label: 'Audit', icon: Document, plane: 'devhub' },
  { to: '/runtime-cockpit', label: 'Runtime cockpit', icon: Report, plane: 'devhub' },
  { to: '/automations', label: 'Automations', icon: Flow, plane: 'devhub' },
  { to: '/ops', label: 'Operations', icon: Events, plane: 'devhub' },
  { to: '/notifications', label: 'Notifications', icon: Notification, plane: 'devhub' },
  { to: '/settings', label: 'Settings', icon: Settings, plane: 'devhub' },
  { to: '/settings', label: 'Layout & white-label', icon: ColorPalette, plane: 'devhub' },
  { to: '/devhub/audit', label: 'Audit & logs', icon: Document, plane: 'devhub' },
];

/** UserHub (TenantPlane / "Workspace") navigation. */
export const USERHUB_NAV: NavModelItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: Dashboard, plane: 'userhub' },
  { to: '/tenant-plane', label: 'Workspace', icon: Application, plane: 'userhub' },
  { to: '/chat', label: 'Chat', icon: Chat, plane: 'userhub' },
  { to: '/documents', label: 'Documents', icon: Document, plane: 'userhub' },
  { to: '/reports', label: 'Reports', icon: Report, plane: 'userhub' },
  { to: '/notifications', label: 'Notifications', icon: Notification, plane: 'userhub' },
  {
    to: '/plugins',
    label: 'Apps & plugins',
    icon: Application,
    plane: 'userhub',
    roleClasses: ['owner', 'admin', 'developer', 'client_admin', 'client_user'],
  },
  { to: '/settings', label: 'Settings', icon: Settings, plane: 'userhub' },
  { to: '/settings', label: 'Brand profile', icon: ColorPalette, plane: 'userhub', comingSoon: true },
];

export function navForPlane(plane: HubPlane): NavModelItem[] {
  return plane === 'devhub' ? DEVHUB_NAV : USERHUB_NAV;
}
